import React, { useState } from "react";

import { Pagination } from "@performyard/py-components";
import { ASYNC_TASK_NAMES } from "@performyard/service-contracts";

import DownloadButtonSSE from "../../../components.smart/DownloadButtonSSE";
import FilterChips from "../../../components/FilterChips";
import PageHeading from "../../../components/PageHeading";
import CompanyMeetingsFilterPanel from "./CompanyMeetingsFilterPanel";
import CompanyMeetingsTable from "./CompanyMeetingsTable";

import ErrorPage from "../../../pages/Error";

import { useMeetingsSearch } from "../../../api/hooks";
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import { useUserAsyncTask } from "../../../hooks/useUserAsyncTask";

import {
	MeetingsSearchSortableFields,
	MeetingsSearchSortableFieldsEnum,
	exportCompanyMeetingsCsv,
} from "../../../api/services/defs/internal-api";

import { convertFiltersToFilterChips, getMeetingsSearchServiceFilters } from "../utils";

import InfoPageTemplate from "../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { config } from "../config";
import { COMPANY_MEETINGS_NAV_LINKS } from "../navigationLinks";
import { CompanyMeetingsFilters } from "../schema";

const PAGE_TITLE = "Company Meetings";

const CompanyMeetings = () => {
	const isNewNav = useIsNewNavigation();
	const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
	const { value: ffMeetings, query: ffMeetingsQuery } = useFeatureFlagQuery("ff-is-meetings-enabled", false);

	const { task: exportTask, triggerTask: triggerExport } = useUserAsyncTask(
		ASYNC_TASK_NAMES.EXPORT_COMPANY_MEETINGS_CSV
	);

	const {
		filters,
		setFilters,
		sort,
		setSort,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
		pageNumber,
		setPageNumber,
	} = useInfoPageTemplateQueryState<CompanyMeetingsFilters>({
		defaultSortName: config.defaultSortName,
		defaultSortDirection: config.defaultSortDirection,
		sortableFields: MeetingsSearchSortableFieldsEnum.options,
		defaultPageNumber: config.defaultPageNumber,
	});

	// Fetch company meetings for admin
	const {
		data: meetings = [],
		isLoading: isFetchingMeetings,
		totalCount: totalMeetingsCount = 0,
	} = useMeetingsSearch<"companyMeetings">({
		...getMeetingsSearchServiceFilters(filters, debouncedSearchValue),
		limit: config.pageSize,
		skip: (pageNumber - 1) * config.pageSize,
		sort: { name: sort.name as MeetingsSearchSortableFields, direction: sort.direction },
	});

	// Wait for feature flag to load, then show access denied if disabled
	if (ffMeetingsQuery.isLoading) {
		return null;
	}

	if (!ffMeetings) {
		return <ErrorPage code={403} />;
	}

	// Handle column header click for sorting
	const handleColumnHeaderClick = (column: { name: string; direction: "asc" | "desc" }) => {
		setSort(column);
	};

	// Handle CSV download
	const handleDownloadCsv = async () => {
		await triggerExport(() =>
			exportCompanyMeetingsCsv({
				filters: getMeetingsSearchServiceFilters(filters, debouncedSearchValue),
				sort: { name: sort.name as MeetingsSearchSortableFields, direction: sort.direction },
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			})
		);
	};

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			searchPlaceholder="Search Meetings..."
			onFilterButtonPress={() => setIsFilterPanelOpen(true)}
			activeFiltersCount={convertFiltersToFilterChips(filters).length}
		/>
	);

	const toolbarInformationRow = (
		<FilterChips
			filters={convertFiltersToFilterChips(filters)}
			label={convertFiltersToFilterChips(filters).length > 0 ? "Filters applied: " : ""}
			onDeleteFilter={(id) => {
				const currentFilters = { ...filters };
				if (id === "status") {
					delete currentFilters.status;
				} else if (id === "dateRange") {
					delete currentFilters.dateRangeStart;
					delete currentFilters.dateRangeEnd;
				} else {
					delete currentFilters[id];
				}
				setFilters({ ...currentFilters });
			}}
		/>
	);

	const filterPanel = (
		<CompanyMeetingsFilterPanel
			isOpen={isFilterPanelOpen}
			onClose={() => setIsFilterPanelOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => {
				setFilters(newFilters);
			}}
		/>
	);

	const mainContent = (
		<div className="space-y-6">
			<CompanyMeetingsTable
				meetings={meetings}
				isFetchingMeetings={isFetchingMeetings}
				activeSort={sort.name}
				activeSortDirection={sort.direction}
				handleColumnHeaderClick={handleColumnHeaderClick}
			/>
			{totalMeetingsCount > 0 && (
				<div className="mx-auto flex w-full max-w-[400px] justify-center">
					<Pagination
						currentPage={pageNumber}
						pageCount={Math.ceil(totalMeetingsCount / config.pageSize)}
						onPageChange={(page: number) => setPageNumber(page)}
					/>
				</div>
			)}
		</div>
	);

	const actionButtons = [
		<DownloadButtonSSE task={exportTask} onClick={handleDownloadCsv} key="download-csv" testid="download-csv">
			Download CSV
		</DownloadButtonSSE>,
	];

	return (
		<InfoPageTemplate
			pageTitle={PAGE_TITLE}
			pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
			mainContent={mainContent}
			actionButtons={actionButtons}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			navigationLinks={COMPANY_MEETINGS_NAV_LINKS}
			activeNavigationLinkKey="all-meetings"
			drawer={filterPanel}
		/>
	);
};

export default CompanyMeetings;
